<p-dialog
    [header]="'Generate base art for all templates'"
    [visible]="visible()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '64rem' }"
    [breakpoints]="{ '1280px': '95vw' }"
    [modal]="true"
    [draggable]="false"
    appendTo="body"
>
    @if (!canManage()) {
    <p-message
        severity="warn"
        text="Only a global admin can generate base art."
    />
    } @else if (creditBlock()) {
    <p-message severity="warn" [text]="creditBlock()!" styleClass="mb-3" />
    }

    <!-- ── Config ─────────────────────────────────────────────────────────── -->
    @if (phase() === 'config') {
    <div class="flex flex-col gap-3">
        <p class="text-sm text-muted-color">
            Generates the missing <b>icon</b> and <b>banner</b> plates for every
            compatible {{ categoryLabel() }} site template. Slots that already
            have content are skipped. Pick a candidate per slot afterwards — it
            saves immediately.
        </p>

        <!-- Semi-auto prompt -->
        <div class="flex flex-col gap-2">
            <label class="font-medium text-sm">Prompt detail (optional)</label>
            <textarea
                pTextarea
                rows="5"
                class="w-full"
                [ngModel]="prompt()"
                (ngModelChange)="prompt.set($event ?? '')"
                placeholder="e.g. emphasise a neon underwater theme"
            ></textarea>
            <small class="text-xs text-muted-color">
                Leave empty for fully automatic art. Any detail here is folded
                into the AI prompt (semi-auto) for every template in this run.
            </small>
        </div>

        @if (preparing()) {
        <div class="flex items-center gap-2 text-muted-color text-sm py-6">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading templates and existing art…</span>
        </div>
        } @else if (targets().length === 0) {
        <p-message
            severity="info"
            text="No compatible site templates were found for this game."
        />
        } @else {
        <div class="flex flex-col gap-2">
            @for (t of targets(); track t.siteTemplateId) {
            <div
                class="flex items-center justify-between gap-3 rounded border border-surface-200 dark:border-surface-700 p-3"
            >
                <div class="flex items-center gap-3 min-w-0">
                    <p-checkbox
                        [binary]="true"
                        [ngModel]="t.include"
                        [disabled]="!!t.blockedReason"
                        (ngModelChange)="
                            toggleInclude(t.siteTemplateId, $event)
                        "
                    />
                    <div class="flex flex-col min-w-0">
                        <span class="font-medium truncate">{{ t.name }}</span>
                        @if (t.blockedReason) {
                        <span class="text-xs text-amber-600">{{
                            t.blockedReason
                        }}</span>
                        } @else {
                        <span class="text-xs text-muted-color">
                            icon:
                            <b [ngClass]="t.hasIcon ? 'text-green-600' : ''">{{
                                t.hasIcon ? 'exists' : 'will generate'
                            }}</b>
                            · banner:
                            <b
                                [ngClass]="t.hasBanner ? 'text-green-600' : ''"
                                >{{
                                    t.hasBanner ? 'exists' : 'will generate'
                                }}</b
                            >
                        </span>
                        }
                    </div>
                </div>
            </div>
            }
        </div>
        }
    </div>

    <ng-template #footer>
        <div class="flex items-center justify-between gap-2 w-full">
            <span class="text-sm text-muted-color">
                {{ plannedCellCount() }} image(s) to generate ·
                {{ includedTargets().length }} template(s)
            </span>
            <div class="flex gap-2">
                <p-button
                    label="Cancel"
                    severity="secondary"
                    outlined
                    (onClick)="close()"
                />
                <p-button
                    label="Generate"
                    icon="pi pi-microchip-ai"
                    [disabled]="!canGenerate()"
                    (onClick)="generate()"
                />
            </div>
        </div>
    </ng-template>
    }

    <!-- ── Results ────────────────────────────────────────────────────────── -->
    @if (phase() === 'results') {
    <div class="flex flex-col gap-5">
        @for (group of cellGroups(); track group.name) {
        <div class="flex flex-col gap-3">
            <div
                class="font-medium pb-1 border-b border-surface-200 dark:border-surface-700"
            >
                {{ group.name }}
            </div>
            @for (cell of group.cells; track cell.key) {
            <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-2">
                    <span class="text-sm font-medium capitalize">{{
                        cell.asset
                    }}</span>
                    <p-button
                        label="Regenerate"
                        icon="pi pi-refresh"
                        size="small"
                        severity="secondary"
                        text
                        [disabled]="cell.status === 'generating' || cell.saving"
                        (onClick)="openRegen(cell.key)"
                    />
                </div>

                @if (cell.status === 'generating') {
                <div
                    class="flex items-center gap-2 text-muted-color text-sm py-3"
                >
                    <p-progress-spinner
                        [style]="{ width: '1.5rem', height: '1.5rem' }"
                        strokeWidth="6"
                    />
                    <span>Generating…</span>
                </div>
                } @else if (cell.status === 'error') {
                <p-message severity="error" [text]="cell.error!" />
                } @else {
                <div class="flex flex-wrap gap-3">
                    @for (cand of cell.candidates; track cand.url) {
                    <button
                        type="button"
                        class="relative rounded border-2 overflow-hidden transition-colors"
                        [ngClass]="
                            isSelected(cell.key, cand.url)
                                ? 'border-primary'
                                : 'border-surface-200 dark:border-surface-700 hover:border-primary/50'
                        "
                        [disabled]="cell.saving"
                        (click)="pick(cell.key, cand)"
                    >
                        <img
                            [src]="cand.url"
                            [alt]="cell.asset + ' candidate'"
                            class="block object-cover"
                            [ngClass]="
                                cell.asset === 'banner'
                                    ? 'w-48 h-28'
                                    : 'w-28 h-28'
                            "
                        />
                        <i
                            class="pi pi-search-plus absolute top-1 left-1 bg-black/50 text-white rounded p-1 text-xs"
                            title="View large"
                            (click)="
                                preview.open(cand.url, cell.asset);
                                $event.stopPropagation()
                            "
                        ></i>
                        @if (isSelected(cell.key, cand.url)) {
                        <span
                            class="absolute top-1 right-1 bg-primary text-primary-contrast rounded-full w-5 h-5 flex items-center justify-center"
                        >
                            <i class="pi pi-check text-xs"></i>
                        </span>
                        }
                    </button>
                    } @empty {
                    <span class="text-sm text-muted-color italic"
                        >No candidates returned.</span
                    >
                    }
                </div>
                @if (cell.saving) {
                <span class="text-xs text-muted-color"
                    ><i class="pi pi-spin pi-spinner"></i> Saving…</span
                >
                }
                }
            </div>
            }
        </div>
        } @empty {
        <p-message
            severity="info"
            text="Nothing to generate — every selected template already has its plates."
        />
        }
    </div>

    <ng-template #footer>
        <div class="flex justify-between gap-2 w-full">
            <p-button
                label="Back"
                icon="pi pi-arrow-left"
                severity="secondary"
                outlined
                (onClick)="backToConfig()"
            />
            <p-button label="Done" icon="pi pi-check" (onClick)="close()" />
        </div>
    </ng-template>
    }
</p-dialog>

<!-- Per-cell regenerate options -->
<p-dialog
    header="Regenerate"
    [visible]="regenVisible()"
    (visibleChange)="regenVisible.set($event)"
    [style]="{ width: '30rem' }"
    [modal]="true"
    [draggable]="false"
    appendTo="body"
>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
            <label class="font-medium text-sm">Samples</label>
            <p-selectbutton
                [options]="sampleOptions"
                optionLabel="label"
                optionValue="value"
                [allowEmpty]="false"
                [ngModel]="regenSamples()"
                (ngModelChange)="regenSamples.set($event)"
            />
        </div>
        <div class="flex flex-col gap-2">
            <label class="font-medium text-sm">Prompt</label>
            <textarea
                pTextarea
                rows="3"
                [ngModel]="regenPrompt()"
                (ngModelChange)="regenPrompt.set($event ?? '')"
                placeholder="Leave empty to re-roll with the same settings"
            ></textarea>
            <div class="flex items-center gap-2">
                <p-checkbox
                    inputId="regen-fullmanual"
                    [binary]="true"
                    [ngModel]="regenFullManual()"
                    (ngModelChange)="regenFullManual.set($event)"
                />
                <label for="regen-fullmanual" class="text-sm"
                    >Replace prompt (full manual)</label
                >
            </div>
            <small class="text-xs text-muted-color">
                Empty = re-roll. With text it refines the AI prompt (semi-auto);
                enable “Replace” to use it as the full prompt.
            </small>
        </div>
    </div>

    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button
                label="Cancel"
                severity="secondary"
                outlined
                (onClick)="regenVisible.set(false)"
            />
            <p-button
                label="Regenerate"
                icon="pi pi-refresh"
                [disabled]="!canRegen()"
                (onClick)="confirmRegen()"
            />
        </div>
    </ng-template>
</p-dialog>
